/**
 * Application Constants
 *
 * Centralized configuration and constant values for the admin panel.
 * This includes API endpoints, WebSocket configuration, theme settings,
 * and application-wide constants.
 *
 * @module constants
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5050/api',
  WS_URL: process.env.REACT_APP_WS_URL || 'ws://localhost:5050',
  TIMEOUT: 30000, // 30 seconds
};

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/players/login',
  REGISTER: '/players/register',
  PROFILE: '/players/profile',

  // Players
  PLAYERS: '/players',
  PLAYER_BY_ID: (id) => `/players/${id}`,
  PLAYER_BALANCE: (id) => `/players/${id}/balance`,

  // Rooms
  ROOMS: '/rooms',
  ROOM_BY_ID: (id) => `/rooms/${id}`,
  ROOM_PLAYERS: (id) => `/rooms/${id}/players`,

  // Games
  GAMES: '/games',
  GAME_BY_ID: (id) => `/games/${id}`,
  GAME_START: (id) => `/games/${id}/start`,
  GAME_CALL_NUMBER: (id) => `/games/${id}/call-number`,
  GAME_END: (id) => `/games/${id}/end`,

  // Bingo Cards
  CARDS: '/bingo-cards',
  CARDS_GENERATE: '/bingo-cards/generate',
  CARD_BY_ID: (id) => `/bingo-cards/${id}`,
  CARD_MARK: (id) => `/bingo-cards/${id}/mark`,
  CARD_VERIFY: (id) => `/bingo-cards/${id}/verify`,
};

// WebSocket Events
export const WS_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  ROOM_CREATED: 'room:created',
  ROOM_UPDATED: 'room:updated',
  ROOM_DELETED: 'room:deleted',
  GAME_STARTED: 'game:started',
  GAME_NUMBER_CALLED: 'game:number_called',
  GAME_ENDED: 'game:ended',
  PLAYER_JOINED: 'player:joined',
  PLAYER_LEFT: 'player:left',
  BINGO_CLAIMED: 'bingo:claimed',
  WINNER_VERIFIED: 'winner:verified',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'bingo_admin_token',
  USER_DATA: 'bingo_admin_user',
  THEME: 'bingo_admin_theme',
  LANGUAGE: 'bingo_admin_language',
};

// Theme Configuration
export const THEME = {
  COLORS: {
    primary: '#9333EA', // Purple
    secondary: '#FFD700', // Gold
    success: '#10B981', // Green
    danger: '#EF4444', // Red
    warning: '#F59E0B', // Orange
    info: '#3B82F6', // Blue
    dark: '#1F2937',
    light: '#F9FAFB',
    background: {
      dark: '#0F172A',
      light: '#FFFFFF',
    },
    text: {
      dark: '#F9FAFB',
      light: '#1F2937',
    },
  },
  GRADIENTS: {
    primary: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
    secondary: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    casino: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)',
  },
};

// Game Configuration
export const GAME_CONFIG = {
  CARD_SIZE: {
    ROWS: 5,
    COLUMNS: 5,
  },
  NUMBER_RANGES: {
    B: { min: 1, max: 15 },
    I: { min: 16, max: 30 },
    N: { min: 31, max: 45 },
    G: { min: 46, max: 60 },
    O: { min: 61, max: 75 },
  },
  MAX_NUMBER: 75,
  MIN_NUMBER: 1,
  CALL_INTERVAL: 5000, // 5 seconds between calls
};

// Winning Patterns
export const WINNING_PATTERNS = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  DIAGONAL: 'diagonal',
  FULL_CARD: 'full_card',
  FOUR_CORNERS: 'four_corners',
  X_PATTERN: 'x_pattern',
};

// Room Status
export const ROOM_STATUS = {
  WAITING: 'waiting',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
};

// Game Status
export const GAME_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

// Player Status
export const PLAYER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

// Date Formats
export const DATE_FORMATS = {
  FULL: 'PPpp', // Jan 1, 2024, 12:00:00 PM
  DATE_ONLY: 'PP', // Jan 1, 2024
  TIME_ONLY: 'p', // 12:00 PM
  SHORT: 'Pp', // Jan 1, 2024, 12:00 PM
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
};

// Chart Configuration
export const CHART_CONFIG = {
  COLORS: [
    '#9333EA', '#FFD700', '#10B981', '#EF4444',
    '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899',
  ],
  DEFAULTS: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  },
};

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Routes
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/',
  ROOMS: '/rooms',
  ROOM_DETAIL: '/rooms/:id',
  ROOM_CREATE: '/rooms/create',
  GAMES: '/games',
  GAME_DETAIL: '/games/:id',
  LIVE_GAME: '/games/:id/live',
  PLAYERS: '/players',
  PLAYER_DETAIL: '/players/:id',
  CARDS: '/cards',
  SETTINGS: '/settings',
};

// User Roles (if implementing role-based access)
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MODERATOR: 'moderator',
  VIEWER: 'viewer',
};

// Permissions
export const PERMISSIONS = {
  CREATE_ROOM: 'create_room',
  DELETE_ROOM: 'delete_room',
  MANAGE_GAMES: 'manage_games',
  MANAGE_PLAYERS: 'manage_players',
  VIEW_REPORTS: 'view_reports',
  MANAGE_BALANCE: 'manage_balance',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Error de conexión. Por favor, verifica tu conexión a internet.',
  UNAUTHORIZED: 'No autorizado. Por favor, inicia sesión nuevamente.',
  FORBIDDEN: 'No tienes permisos para realizar esta acción.',
  NOT_FOUND: 'Recurso no encontrado.',
  SERVER_ERROR: 'Error del servidor. Por favor, intenta más tarde.',
  VALIDATION_ERROR: 'Error de validación. Por favor, verifica los datos ingresados.',
  TIMEOUT: 'La solicitud ha excedido el tiempo de espera.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Inicio de sesión exitoso',
  LOGOUT: 'Sesión cerrada correctamente',
  ROOM_CREATED: 'Sala creada exitosamente',
  ROOM_UPDATED: 'Sala actualizada exitosamente',
  ROOM_DELETED: 'Sala eliminada exitosamente',
  GAME_STARTED: 'Juego iniciado exitosamente',
  GAME_ENDED: 'Juego finalizado exitosamente',
  PLAYER_UPDATED: 'Jugador actualizado exitosamente',
  BALANCE_UPDATED: 'Saldo actualizado exitosamente',
};

// Regular Expressions for Validation
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/,
};

export default {
  API_CONFIG,
  API_ENDPOINTS,
  WS_EVENTS,
  STORAGE_KEYS,
  THEME,
  GAME_CONFIG,
  WINNING_PATTERNS,
  ROOM_STATUS,
  GAME_STATUS,
  PLAYER_STATUS,
  PAGINATION,
  DATE_FORMATS,
  CHART_CONFIG,
  NOTIFICATION_TYPES,
  ROUTES,
  USER_ROLES,
  PERMISSIONS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  REGEX,
};
